/**
 * Etrenzik Design — vertical definitions.
 *
 * This is product content, not infrastructure. Adding a vertical or a question
 * should never require a database migration: project answers are stored as JSON
 * keyed by the question `id` values below.
 *
 * Every number in this file is a planning rule of thumb for the Southeast US
 * market. They exist to get an owner to a defensible starting point, not to
 * replace a design professional. Surface them as ranges, always.
 */

import { VERTICAL_DETAIL } from './vertical-questions';

export type ConstructionSystem =
  | 'pemb'            // pre-engineered metal building
  | 'tilt-up'         // site-cast tilt-up concrete
  | 'masonry'         // CMU / brick load-bearing
  | 'steel-frame'     // conventional structural steel + skin
  | 'wood-frame'      // light wood frame
  | 'cold-formed'     // light-gauge steel frame
  | 'icf'             // insulated concrete forms
  | 'precast'         // precast concrete panels
  | 'mass-timber'     // CLT / glulam
  | 'hybrid';         // steel structure, masonry front elevation, etc.

export const CONSTRUCTION_SYSTEMS: Record<
  ConstructionSystem,
  { label: string; blurb: string; relativeCost: 1 | 2 | 3 | 4; speed: 1 | 2 | 3 | 4 }
> = {
  pemb: {
    label: 'Pre-engineered metal building',
    blurb:
      'Steel frame and metal panel, shipped as a kit. The fastest and usually the ' +
      'least expensive way to enclose square footage. Add a masonry or storefront ' +
      'front elevation if you want it to read as something other than a metal building.',
    relativeCost: 1,
    speed: 4,
  },
  'tilt-up': {
    label: 'Tilt-up concrete',
    blurb:
      'Wall panels cast flat on the slab and lifted into place. Efficient above ' +
      'roughly 30,000 sf, very durable, and it takes reveals and paint well.',
    relativeCost: 2,
    speed: 3,
  },
  masonry: {
    label: 'Masonry (block and brick)',
    blurb:
      'Load-bearing block, often with a brick or split-face veneer. Reads as ' +
      'permanent and traditional, holds up in hard-use buildings, slower to build.',
    relativeCost: 3,
    speed: 2,
  },
  'steel-frame': {
    label: 'Structural steel frame',
    blurb:
      'Conventional steel with your choice of skin. The most flexible option for ' +
      'long spans, multiple stories, and irregular shapes.',
    relativeCost: 3,
    speed: 3,
  },
  'wood-frame': {
    label: 'Wood frame',
    blurb:
      'Cost-effective for smaller single-story buildings and anywhere you want a ' +
      'warmer, more residential feel. Height and area are limited by code.',
    relativeCost: 1,
    speed: 3,
  },
  'cold-formed': {
    label: 'Light-gauge steel frame',
    blurb:
      'Wood-frame construction methods with steel studs. Non-combustible, dimensionally ' +
      'stable, common in hotels, senior living, and multi-story small commercial.',
    relativeCost: 2,
    speed: 3,
  },
  icf: {
    label: 'Insulated concrete forms',
    blurb:
      'Concrete walls poured into permanent foam forms. Excellent envelope, strong ' +
      'storm performance, and low operating cost. Higher first cost.',
    relativeCost: 3,
    speed: 2,
  },
  precast: {
    label: 'Precast concrete panels',
    blurb:
      'Factory-cast panels erected on site. Tight quality control, fast erection, and ' +
      'a wide range of finishes. Needs crane access and a clear delivery route.',
    relativeCost: 3,
    speed: 4,
  },
  'mass-timber': {
    label: 'Mass timber',
    blurb:
      'Engineered heavy timber left exposed. Distinctive, warm, and a strong ' +
      'sustainability story. Fewer regional suppliers, so verify cost early.',
    relativeCost: 4,
    speed: 3,
  },
  hybrid: {
    label: 'Hybrid',
    blurb:
      'The most common real-world answer. Economical structure behind, with brick, ' +
      'stone, or storefront on the elevations the public sees.',
    relativeCost: 2,
    speed: 3,
  },
};

export type QuestionType = 'number' | 'select' | 'multi' | 'range' | 'boolean' | 'text';

export interface Question {
  id: string;
  label: string;
  help?: string;
  type: QuestionType;
  options?: { value: string; label: string; help?: string }[];
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  required?: boolean;
  /** Only show this question when another answer matches. */
  showIf?: { questionId: string; equals: string | number | boolean };
}

export interface SizeTranche {
  id: string;
  label: string;
  minSf: number;
  maxSf: number;
  /** Plain-language anchor so a non-technical owner can picture it. */
  anchor: string;
}

export interface Vertical {
  id: string;
  label: string;
  group: string;
  blurb: string;
  /** Suggested to the customer first; all systems remain selectable. */
  commonSystems: ConstructionSystem[];
  tranches: SizeTranche[];
  questions: Question[];
  /**
   * Derive an estimated square footage from questionnaire answers, when the
   * vertical has a reliable driver (seats, beds, racks, students). Return null
   * when the customer should just pick a tranche.
   */
  deriveSf?: (a: Record<string, unknown>) => { low: number; high: number; basis: string } | null;
  /** Rough connected electrical load, W/sf, for the lighting & power preview. */
  wattsPerSf: [number, number];
}

/** Generic bands used by verticals without their own scale. */
export const DEFAULT_TRANCHES: SizeTranche[] = [
  { id: 'xs', label: 'Under 5,000 sf', minSf: 0, maxSf: 5000, anchor: 'A single-tenant shop or small office' },
  { id: 'sm', label: '5,000 – 15,000 sf', minSf: 5000, maxSf: 15000, anchor: 'A neighborhood retail building' },
  { id: 'md', label: '15,000 – 40,000 sf', minSf: 15000, maxSf: 40000, anchor: 'A grocery-anchored strip center' },
  { id: 'lg', label: '40,000 – 100,000 sf', minSf: 40000, maxSf: 100000, anchor: 'A big-box store' },
  { id: 'xl', label: '100,000 – 300,000 sf', minSf: 100000, maxSf: 300000, anchor: 'A regional distribution center' },
  { id: 'xxl', label: 'Over 300,000 sf', minSf: 300000, maxSf: 1_500_000, anchor: 'A campus or bulk warehouse' },
];

/** Questions asked on every vertical, appended after the vertical-specific set. */
export const COMMON_QUESTIONS: Question[] = [
  {
    id: 'site_status',
    label: 'Where are you with the site?',
    type: 'select',
    required: true,
    options: [
      { value: 'own', label: 'We own the land' },
      { value: 'under-contract', label: 'Under contract' },
      { value: 'looking', label: 'Still looking' },
      { value: 'existing', label: 'Renovating or adding to a building we already have' },
    ],
  },
  {
    id: 'timeline',
    label: 'When do you want to be in the building?',
    type: 'select',
    required: true,
    options: [
      { value: 'lt12', label: 'Within a year' },
      { value: '1-2y', label: 'One to two years' },
      { value: '2-4y', label: 'Two to four years' },
      { value: 'exploring', label: 'Just exploring for now' },
    ],
  },
  {
    id: 'stories',
    label: 'How many stories?',
    type: 'select',
    options: [
      { value: '1', label: 'One' },
      { value: '2', label: 'Two' },
      { value: '3+', label: 'Three or more' },
      { value: 'unsure', label: 'Not sure yet' },
    ],
  },
  {
    id: 'priorities',
    label: 'What matters most? Pick up to three.',
    type: 'multi',
    max: 3,
    options: [
      { value: 'first-cost', label: 'Lowest cost to build' },
      { value: 'operating-cost', label: 'Lowest cost to operate' },
      { value: 'speed', label: 'Fastest to open' },
      { value: 'curb-appeal', label: 'Curb appeal' },
      { value: 'durability', label: 'Durability and low maintenance' },
      { value: 'expandable', label: 'Room to expand later' },
      { value: 'sustainability', label: 'Energy performance and sustainability' },
    ],
  },
  {
    id: 'future_phase',
    label: 'Do you expect to expand this building later?',
    type: 'boolean',
    help: 'If yes, we will show you where to leave room and what to stub out now.',
  },
  {
    id: 'ev_solar',
    label: 'Interested in EV charging or rooftop solar?',
    type: 'multi',
    options: [
      { value: 'ev-now', label: 'EV charging at opening' },
      { value: 'ev-ready', label: 'Conduit for EV charging later' },
      { value: 'solar-now', label: 'Solar at opening' },
      { value: 'solar-ready', label: 'Structure and conduit for solar later' },
      { value: 'generator', label: 'Standby generator' },
      { value: 'none', label: 'None of these' },
    ],
  },
];

export const VERTICALS: Vertical[] = [
  // ─── Worship ──────────────────────────────────────────────────────────────
  {
    id: 'worship',
    label: 'Church & worship',
    group: 'Community & institutional',
    blurb: 'Sanctuaries, family life centers, education wings, and multi-purpose worship buildings.',
    commonSystems: ['pemb', 'masonry', 'hybrid', 'steel-frame', 'wood-frame'],
    wattsPerSf: [3, 6],
    tranches: [
      { id: 'w1', label: 'Under 8,000 sf', minSf: 0, maxSf: 8000, anchor: 'Roughly 150–250 seats plus a lobby' },
      { id: 'w2', label: '8,000 – 18,000 sf', minSf: 8000, maxSf: 18000, anchor: '250–500 seats with classrooms' },
      { id: 'w3', label: '18,000 – 40,000 sf', minSf: 18000, maxSf: 40000, anchor: '500–1,200 seats with a family life center' },
      { id: 'w4', label: '40,000 – 80,000 sf', minSf: 40000, maxSf: 80000, anchor: 'A multi-building campus' },
      { id: 'w5', label: 'Over 80,000 sf', minSf: 80000, maxSf: 250000, anchor: 'A large multi-site campus' },
    ],
    questions: [
      {
        id: 'congregation',
        label: 'How many people attend on a typical weekend?',
        type: 'number',
        min: 25,
        max: 15000,
        step: 25,
        unit: 'people',
        required: true,
        help: 'Count everyone across all services. We will size the room from this.',
      },
      {
        id: 'services',
        label: 'How many services do you hold on a weekend?',
        type: 'select',
        options: [
          { value: '1', label: 'One' },
          { value: '2', label: 'Two' },
          { value: '3+', label: 'Three or more' },
        ],
        help: 'Multiple services mean you can build a smaller room.',
      },
      {
        id: 'growth',
        label: 'What attendance do you want the building to hold in ten years?',
        type: 'number',
        min: 25,
        max: 20000,
        step: 25,
        unit: 'people',
      },
      {
        id: 'seating',
        label: 'How do you want people seated?',
        type: 'select',
        options: [
          { value: 'pews', label: 'Pews' },
          { value: 'chairs', label: 'Movable chairs' },
          { value: 'theater', label: 'Theater-style fixed seating' },
        ],
      },
      {
        id: 'sanctuary_shape',
        label: 'What shape should the worship space be?',
        type: 'select',
        options: [
          { value: 'longitudinal', label: 'Long and narrow, altar at the far end' },
          { value: 'fan', label: 'Fan-shaped, everyone close to the front' },
          { value: 'theater', label: 'Theater in the round or thrust stage' },
          { value: 'flexible', label: 'Flat floor, rearrangeable' },
        ],
      },
      {
        id: 'spaces',
        label: 'What else goes in the building?',
        type: 'multi',
        options: [
          { value: 'lobby', label: 'Large gathering lobby' },
          { value: 'classrooms', label: 'Classrooms and Sunday school' },
          { value: 'nursery', label: 'Nursery and children’s wing' },
          { value: 'youth', label: 'Youth space' },
          { value: 'gym', label: 'Gym or family life center' },
          { value: 'kitchen', label: 'Commercial kitchen' },
          { value: 'fellowship', label: 'Fellowship hall' },
          { value: 'offices', label: 'Church offices' },
          { value: 'cafe', label: 'Café or coffee bar' },
          { value: 'baptistry', label: 'Baptistry or baptismal pool' },
          { value: 'broadcast', label: 'Broadcast and streaming control room' },
        ],
      },
      {
        id: 'av_intensity',
        label: 'How production-heavy is your worship service?',
        type: 'select',
        help: 'This drives ceiling height, rigging, electrical service, and lighting.',
        options: [
          { value: 'traditional', label: 'Traditional — organ, piano, choir, natural light' },
          { value: 'blended', label: 'Blended — a band, screens, moderate lighting' },
          { value: 'production', label: 'Full production — stage lighting, LED wall, live streaming' },
        ],
      },
      {
        id: 'exterior_read',
        label: 'When someone drives by, what should it look like?',
        type: 'select',
        options: [
          { value: 'unmistakably-church', label: 'Unmistakably a church — steeple, gable, traditional' },
          { value: 'welcoming-modern', label: 'Modern and welcoming, not obviously a church' },
          { value: 'civic', label: 'Civic and dignified, like a community landmark' },
          { value: 'warm-informal', label: 'Warm and informal, like a lodge or barn' },
        ],
      },
    ],
    /**
     * Worship area runs roughly 15–20 sf per seat including aisles and platform.
     * Whole-building totals land near 30–45 sf per seat once lobby, classrooms,
     * children's space, and back of house are included. Multiple services reduce
     * the required seat count.
     */
    deriveSf: (a) => {
      const attendance = Number(a.growth ?? a.congregation);
      if (!attendance || Number.isNaN(attendance)) return null;
      const serviceDivisor = a.services === '3+' ? 2.4 : a.services === '2' ? 1.7 : 1;
      // Size the room for a comfortably full house, not a packed one.
      const seats = Math.ceil((attendance / serviceDivisor) * 1.15);
      const extras = Array.isArray(a.spaces) ? a.spaces.length : 0;
      const lowPerSeat = 28 + extras * 1.2;
      const highPerSeat = 40 + extras * 1.8;
      return {
        low: Math.round((seats * lowPerSeat) / 500) * 500,
        high: Math.round((seats * highPerSeat) / 500) * 500,
        basis: `About ${seats.toLocaleString()} seats, plus lobby, children's space, and back of house.`,
      };
    },
  },

  // ─── Warehouse & distribution ─────────────────────────────────────────────
  {
    id: 'warehouse',
    label: 'Warehouse & distribution',
    group: 'Industrial',
    blurb: 'Bulk storage, distribution centers, cross-dock, and flex industrial.',
    commonSystems: ['pemb', 'tilt-up', 'precast', 'steel-frame'],
    wattsPerSf: [1.5, 4],
    tranches: DEFAULT_TRANCHES,
    questions: [
      {
        id: 'use',
        label: 'What happens inside?',
        type: 'select',
        required: true,
        options: [
          { value: 'bulk', label: 'Bulk storage' },
          { value: 'distribution', label: 'Distribution and order fulfillment' },
          { value: 'cross-dock', label: 'Cross-dock' },
          { value: 'cold', label: 'Cold storage or refrigerated' },
          { value: 'flex', label: 'Flex — part warehouse, part office or shop' },
        ],
      },
      {
        id: 'clear_height',
        label: 'How tall do you need the ceiling?',
        type: 'select',
        required: true,
        help: 'Clear height under the lowest obstruction. It drives racking, cost, and sprinklers.',
        options: [
          { value: '16-20', label: '16 to 20 feet — light storage, single-level racking' },
          { value: '24-28', label: '24 to 28 feet — standard modern warehouse' },
          { value: '32-36', label: '32 to 36 feet — high-cube distribution' },
          { value: '40+', label: '40 feet or more — automated storage' },
          { value: 'unsure', label: 'Not sure' },
        ],
      },
      { id: 'dock_doors', label: 'How many dock doors?', type: 'number', min: 0, max: 200, step: 1 },
      { id: 'drive_in', label: 'How many drive-in ramps?', type: 'number', min: 0, max: 20, step: 1 },
      { id: 'office_sf', label: 'How much office space inside?', type: 'number', min: 0, max: 60000, step: 500, unit: 'sf' },
      {
        id: 'trailer_parking',
        label: 'Do you need trailer parking or a truck court?',
        type: 'select',
        options: [
          { value: 'none', label: 'No' },
          { value: 'small', label: 'A few trailer stalls' },
          { value: 'full', label: 'A full truck court, 130 feet deep' },
        ],
      },
      {
        id: 'special',
        label: 'Anything special inside?',
        type: 'multi',
        options: [
          { value: 'racking', label: 'Heavy racking' },
          { value: 'mezzanine', label: 'Mezzanine' },
          { value: 'conveyor', label: 'Conveyor or automation' },
          { value: 'esfr', label: 'ESFR sprinkler' },
          { value: 'hazmat', label: 'Hazardous materials storage' },
          { value: 'crane', label: 'Overhead crane' },
          { value: 'battery', label: 'Forklift battery charging' },
        ],
      },
      {
        id: 'front_elevation',
        label: 'How finished should the front look?',
        type: 'select',
        options: [
          { value: 'utilitarian', label: 'Purely functional — metal panel is fine' },
          { value: 'clean', label: 'Clean and corporate — painted panel, glass entry' },
          { value: 'showpiece', label: 'A showpiece office front — masonry and storefront glass' },
        ],
      },
    ],
  },

  // ─── Data center ──────────────────────────────────────────────────────────
  {
    id: 'data-center',
    label: 'Data center',
    group: 'Industrial',
    blurb: 'Enterprise, colocation, and edge compute facilities.',
    commonSystems: ['tilt-up', 'precast', 'steel-frame', 'masonry'],
    wattsPerSf: [50, 400],
    tranches: [
      { id: 'edge', label: 'Edge — under 5,000 sf', minSf: 0, maxSf: 5000, anchor: 'A handful of racks close to the users' },
      { id: 'ent', label: 'Enterprise — 5,000 to 25,000 sf', minSf: 5000, maxSf: 25000, anchor: 'A single-company facility' },
      { id: 'colo', label: 'Colocation — 25,000 to 100,000 sf', minSf: 25000, maxSf: 100000, anchor: 'Multi-tenant halls' },
      { id: 'hyper', label: 'Hyperscale — over 100,000 sf', minSf: 100000, maxSf: 1_000_000, anchor: 'Campus scale' },
    ],
    questions: [
      { id: 'it_load_mw', label: 'How much IT load, in megawatts?', type: 'number', min: 0.1, max: 500, step: 0.1, unit: 'MW', required: true },
      { id: 'racks', label: 'How many racks?', type: 'number', min: 1, max: 20000, step: 10 },
      { id: 'rack_density', label: 'Average density per rack?', type: 'select', options: [
        { value: 'lt10', label: 'Under 10 kW — traditional enterprise' },
        { value: '10-25', label: '10 to 25 kW — modern mixed' },
        { value: '25-50', label: '25 to 50 kW — dense compute' },
        { value: 'gt50', label: 'Over 50 kW — AI and HPC, liquid cooling' },
      ]},
      { id: 'tier', label: 'What redundancy level?', type: 'select', options: [
        { value: 'n', label: 'N — no redundancy' },
        { value: 'n1', label: 'N+1' },
        { value: '2n', label: '2N — fully concurrent maintainable' },
        { value: '2n1', label: '2N+1 — fault tolerant' },
      ]},
      { id: 'cooling', label: 'How will you cool it?', type: 'select', options: [
        { value: 'crac', label: 'Air — CRAC or CRAH with containment' },
        { value: 'evap', label: 'Evaporative or adiabatic' },
        { value: 'liquid', label: 'Direct liquid to chip' },
        { value: 'immersion', label: 'Immersion' },
        { value: 'unsure', label: 'Not sure yet' },
      ]},
      { id: 'power_source', label: 'Utility service status?', type: 'select', options: [
        { value: 'confirmed', label: 'Capacity confirmed with the utility' },
        { value: 'in-progress', label: 'In discussion with the utility' },
        { value: 'not-started', label: 'Not started' },
      ], help: 'Power availability, not square footage, is usually what decides the site.' },
      { id: 'security', label: 'Security posture?', type: 'select', options: [
        { value: 'standard', label: 'Standard commercial' },
        { value: 'hardened', label: 'Hardened — anti-ram perimeter, mantraps' },
        { value: 'gov', label: 'Government or regulated' },
      ]},
    ],
    deriveSf: (a) => {
      const mw = Number(a.it_load_mw);
      if (!mw || Number.isNaN(mw)) return null;
      // Whole-building area including electrical, mechanical, and support space.
      return {
        low: Math.round((mw * 8000) / 1000) * 1000,
        high: Math.round((mw * 15000) / 1000) * 1000,
        basis: `${mw} MW of IT load, including electrical rooms, mechanical, and support space.`,
      };
    },
  },

  // ─── Retail & strip centers ───────────────────────────────────────────────
  {
    id: 'retail',
    label: 'Retail & strip centers',
    group: 'Commercial',
    blurb: 'Strip centers, neighborhood centers, single-tenant retail, and outparcels.',
    commonSystems: ['masonry', 'pemb', 'hybrid', 'tilt-up', 'wood-frame'],
    wattsPerSf: [4, 8],
    tranches: DEFAULT_TRANCHES,
    questions: [
      { id: 'format', label: 'What kind of center?', type: 'select', required: true, options: [
        { value: 'strip', label: 'Inline strip — several small tenants' },
        { value: 'neighborhood', label: 'Neighborhood center with an anchor' },
        { value: 'single', label: 'Single tenant, freestanding' },
        { value: 'outparcel', label: 'Outparcel — small pad building' },
        { value: 'mixed', label: 'Mixed use — retail below, offices or apartments above' },
      ]},
      { id: 'tenant_count', label: 'How many tenant spaces?', type: 'number', min: 1, max: 60, step: 1 },
      { id: 'bay_width', label: 'Typical tenant bay width?', type: 'select', options: [
        { value: '20', label: '20 feet — small shops' },
        { value: '25', label: '25 feet — standard' },
        { value: '40', label: '40 feet or wider' },
        { value: 'mixed', label: 'A mix' },
      ]},
      { id: 'drive_thru', label: 'How many drive-thru lanes?', type: 'number', min: 0, max: 8, step: 1 },
      { id: 'restaurant', label: 'Will any tenants be restaurants?', type: 'boolean', help: 'Restaurants change grease exhaust, gas, water, and electrical service.' },
      { id: 'facade_rhythm', label: 'How should the storefront read?', type: 'select', options: [
        { value: 'uniform', label: 'One uniform facade across all tenants' },
        { value: 'varied', label: 'Varied heights and materials, like a small main street' },
        { value: 'anchor', label: 'A tall anchor element with lower wings' },
      ]},
      { id: 'signage', label: 'Signage approach?', type: 'select', options: [
        { value: 'band', label: 'A continuous sign band' },
        { value: 'individual', label: 'Individual tenant panels' },
        { value: 'monument', label: 'A monument sign plus building signs' },
      ]},
    ],
  },

  // ─── Office ───────────────────────────────────────────────────────────────
  {
    id: 'office',
    label: 'Office & professional',
    group: 'Commercial',
    blurb: 'Corporate headquarters, professional offices, medical office buildings, and flex office.',
    commonSystems: ['steel-frame', 'masonry', 'hybrid', 'cold-formed', 'precast'],
    wattsPerSf: [5, 9],
    tranches: DEFAULT_TRANCHES,
    questions: [
      { id: 'headcount', label: 'How many people will work here?', type: 'number', min: 2, max: 5000, step: 5, unit: 'people', required: true },
      { id: 'work_style', label: 'How do they work?', type: 'select', options: [
        { value: 'private', label: 'Mostly private offices' },
        { value: 'open', label: 'Mostly open workstations' },
        { value: 'hybrid', label: 'Hybrid — desks shared, lots of meeting rooms' },
        { value: 'call-center', label: 'Call center density' },
      ]},
      { id: 'meeting', label: 'How much meeting and collaboration space?', type: 'select', options: [
        { value: 'light', label: 'A conference room or two' },
        { value: 'moderate', label: 'Several rooms plus a break area' },
        { value: 'heavy', label: 'Training rooms, boardroom, café, and huddle rooms' },
      ]},
      { id: 'client_facing', label: 'Do clients come to the building?', type: 'boolean' },
      { id: 'shop', label: 'Any shop, warehouse, or yard attached?', type: 'boolean' },
    ],
    deriveSf: (a) => {
      const n = Number(a.headcount);
      if (!n || Number.isNaN(n)) return null;
      const perPerson: Record<string, [number, number]> = {
        private: [225, 300],
        open: [130, 190],
        hybrid: [110, 170],
        'call-center': [80, 125],
      };
      const [lo, hi] = perPerson[String(a.work_style)] ?? [150, 230];
      const meetingBump = a.meeting === 'heavy' ? 1.25 : a.meeting === 'moderate' ? 1.1 : 1;
      return {
        low: Math.round((n * lo * meetingBump) / 250) * 250,
        high: Math.round((n * hi * meetingBump) / 250) * 250,
        basis: `${n.toLocaleString()} people, including circulation, meeting space, and support.`,
      };
    },
  },

  // ─── Remaining verticals ──────────────────────────────────────────────────
  // Identity, systems, and electrical load live in the table below. The
  // questions, size bands, and sizing rules for each live in
  // `vertical-questions.ts`, which keeps this file readable as the roster.
  ...([
    ['medical', 'Medical & healthcare', 'Community & institutional', 'Clinics, urgent care, dental, imaging, surgery centers, and medical office.', ['masonry', 'steel-frame', 'cold-formed', 'hybrid'], [6, 12]],
    ['education', 'Schools & education', 'Community & institutional', 'K-12 buildings, additions, gyms, career academies, and higher-ed facilities.', ['masonry', 'steel-frame', 'precast', 'hybrid'], [3, 7]],
    ['municipal', 'Municipal & civic', 'Community & institutional', 'City halls, courthouses, police and fire stations, public works, and libraries.', ['masonry', 'steel-frame', 'precast', 'icf'], [3, 8]],
    ['manufacturing', 'Manufacturing & industrial', 'Industrial', 'Production plants, fabrication shops, and process facilities.', ['pemb', 'tilt-up', 'steel-frame', 'precast'], [8, 40]],
    ['automotive', 'Automotive', 'Commercial', 'Dealerships, service and collision centers, tire and quick-lube, and car washes.', ['pemb', 'steel-frame', 'masonry', 'hybrid'], [4, 10]],
    ['restaurant', 'Restaurants & food service', 'Commercial', 'Quick service, fast casual, full service, breweries, and ghost kitchens.', ['wood-frame', 'masonry', 'cold-formed', 'hybrid'], [15, 35]],
    ['hospitality', 'Hotels & event venues', 'Commercial', 'Limited and select service hotels, event centers, and banquet halls.', ['cold-formed', 'masonry', 'steel-frame', 'precast'], [4, 9]],
    ['recreation', 'Sports & recreation', 'Community & institutional', 'Gyms, fieldhouses, courts, aquatics, and community recreation centers.', ['pemb', 'steel-frame', 'masonry', 'hybrid'], [3, 8]],
    ['self-storage', 'Self storage', 'Industrial', 'Single-story drive-up, multi-story climate controlled, and boat and RV storage.', ['pemb', 'cold-formed', 'masonry', 'precast'], [1, 3]],
    ['multifamily', 'Multi-family & mixed use', 'Residential', 'Apartments, townhomes, and buildings with retail below and residences above.', ['wood-frame', 'cold-formed', 'precast', 'steel-frame'], [4, 8]],
    ['senior-living', 'Senior living', 'Residential', 'Independent living, assisted living, and memory care.', ['cold-formed', 'wood-frame', 'masonry', 'icf'], [5, 10]],
    ['childcare', 'Childcare & early learning', 'Community & institutional', 'Daycare centers, preschools, and after-school facilities.', ['wood-frame', 'masonry', 'cold-formed', 'hybrid'], [4, 8]],
    ['veterinary', 'Veterinary & animal care', 'Commercial', 'Vet clinics, animal hospitals, boarding, and shelters.', ['masonry', 'cold-formed', 'pemb', 'hybrid'], [6, 12]],
    ['aviation', 'Aviation & hangars', 'Industrial', 'Corporate hangars, maintenance facilities, and FBO terminals.', ['pemb', 'steel-frame'], [2, 6]],
    ['agricultural', 'Agricultural & controlled environment', 'Industrial', 'Equipment barns, processing buildings, greenhouses, and indoor growing.', ['pemb', 'steel-frame'], [3, 60]],
    ['utility', 'Utility, substation & EV', 'Industrial', 'Control buildings, substations, EV charging hubs, and battery storage.', ['masonry', 'precast', 'icf', 'pemb'], [10, 100]],
    ['convenience', 'Convenience & fuel', 'Commercial', 'C-stores, truck stops, fuel canopies, and travel centers.', ['masonry', 'hybrid', 'wood-frame'], [15, 30]],
    ['bank', 'Banks & credit unions', 'Commercial', 'Branches, operations centers, and drive-up facilities.', ['masonry', 'steel-frame', 'hybrid'], [5, 10]],
    ['laboratory', 'Laboratory & R&D', 'Industrial', 'Wet and dry labs, testing facilities, and research buildings.', ['steel-frame', 'masonry', 'precast'], [12, 30]],
    ['funeral', 'Funeral homes', 'Commercial', 'Funeral homes, chapels, and crematories.', ['masonry', 'wood-frame', 'hybrid'], [3, 7]],
    ['flex', 'Flex & light industrial condo', 'Industrial', 'Small-bay flex, contractor condos, and incubator space.', ['pemb', 'tilt-up', 'masonry'], [3, 8]],
    ['museum', 'Museums & cultural', 'Community & institutional', 'Museums, galleries, performing arts, and visitor centers.', ['steel-frame', 'masonry', 'precast', 'mass-timber'], [4, 10]],
  ] as const).map(
    ([id, label, group, blurb, systems, watts]): Vertical => ({
      id,
      label,
      group,
      blurb,
      commonSystems: systems as unknown as ConstructionSystem[],
      wattsPerSf: watts as unknown as [number, number],
      tranches: VERTICAL_DETAIL[id]?.tranches ?? DEFAULT_TRANCHES,
      questions: VERTICAL_DETAIL[id]?.questions ?? [],
      deriveSf: VERTICAL_DETAIL[id]?.deriveSf,
    }),
  ),
];

export const VERTICAL_GROUPS = [
  'Community & institutional',
  'Commercial',
  'Industrial',
  'Residential',
] as const;

export function getVertical(id: string): Vertical | undefined {
  return VERTICALS.find((v) => v.id === id);
}

/**
 * Planning rules of thumb for the site and parking calculator. Always present as
 * a range, always paired with "confirm with your local zoning ordinance."
 */
export const PARKING_RATIOS: Record<string, { per: string; low: number; high: number }> = {
  worship: { per: 'seat', low: 0.25, high: 0.4 },
  warehouse: { per: '1,000 sf', low: 0.3, high: 0.8 },
  'data-center': { per: '1,000 sf', low: 0.1, high: 0.4 },
  retail: { per: '1,000 sf', low: 3.5, high: 5 },
  office: { per: '1,000 sf', low: 3, high: 4.5 },
  medical: { per: '1,000 sf', low: 4, high: 6 },
  restaurant: { per: 'seat', low: 0.3, high: 0.5 },
  education: { per: 'staff + student driver', low: 0.9, high: 1.2 },
  recreation: { per: '1,000 sf', low: 3, high: 5 },
  hospitality: { per: 'room', low: 1, high: 1.3 },
  'self-storage': { per: '1,000 sf', low: 0.1, high: 0.3 },
};

/** Square feet of paved area per parking space, including drive aisles. */
export const SF_PER_PARKING_SPACE: [number, number] = [320, 400];
